import { CustomTooltip } from '@/components/custom-tooltip';
import { LoadingBar } from '@/components/loading-bar/loading-bar';
import { PendingRecsStatusMessage } from '@/features/rate-change/components/pending-recs-status-message';
import { RateChangeJobStatus } from '@/features/rate-change/components/rate-change-job-status';
import { isRecStatusAutopilotPending } from '@/helpers/recStatusHelper';
import useWindowDimensions from '@/hooks/window-size';
import type { AskRevFilter } from '@/types/askRevFilters';
import {
	DndContext,
	type DragEndEvent,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import { HelpOutline } from '@mui/icons-material';
import { Switch } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import type {
	EditableCallbackParams,
	ICellEditorParams,
	ICellRendererParams,
	NewValueParams,
	ValueFormatterParams,
	ValueSetterParams,
} from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import styles from 'common/_variables.module.scss';
import { DropdownList } from 'components/DropdownList/DropdownList';
import { ExportCSV } from 'components/ExportCSV/ExportCSV';
import { Modal } from 'components/Modal/Modal';
import { useModal } from 'components/Modal/useModal';
import { useExportContext } from 'context/exportContext';
import { usePropertyContext } from 'context/propertyContext';
import { alertAdded } from 'features/alerts/redux/alerts-slice';
import {
	addDaysStr,
	dataDate,
	dayRange,
	today,
	todayAdd,
	todaySubtract,
} from 'features/dates/date-helpers';
import { selectShowAskRev } from 'features/header/redux/selectors';
import { setMyMetrics, setTabs } from 'features/my-view/redux/my-view-slice';
import { selectRecReviewTab } from 'features/my-view/redux/selectors';
import { selectProperty } from 'features/property/redux/selectors';
import { RateUploadButton } from 'features/rate-upload/components/rate-upload-button';
import { useRateUpload } from 'features/rate-upload/hooks/use-rate-upload';
import {
	selectAllRowsSelected,
	selectPendingRecs,
	selectPolling,
	selectUploading,
} from 'features/rate-upload/redux/rate-upload-selectors';
import { setAllRowsSelected } from 'features/rate-upload/redux/rate-upload-slice';
import {
	useGetIntradayForecastQuery,
	useGetRecRateQueryExplainersQuery,
} from 'features/rec-rates/gql/_gen_/property-rec-rates.gql';
import { useUser } from 'features/users/context/userContext';
import { SettingsScope } from 'graphql/gql-types';
import { getCsvFormatHeader } from 'helpers/csvHelpers';
import { labels } from 'locales/en.label';
import { pickBy } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BsFillFileBarGraphFill } from 'react-icons/bs';
import { IoStar } from 'react-icons/io5';
import {
	MdCheckCircleOutline,
	MdMoreHoriz,
	MdRefresh,
	MdSimCardDownload,
} from 'react-icons/md';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import styled from 'styled-components';
import { DashboardTable } from './DashboardTable';
import { FavStayDate } from './FavStayDate';
import { RateCellRender } from './RateCellRender';
import { RecAlert } from './RecAlert';
import { RestrictionCell } from './RestrictionCell';
import { StayDateWithSeasonTooltip } from './StayDateWithSeasonTooltip';
import ForecastInfoTooltip from './components/forecast-info-tooltip/forecast-info-tooltip';
import PriceDecisionTooltip from './components/price-decision-tooltip/price-decision-tooltip';
import { RateShopModal } from './components/rate-shop-modal/rate-shop-modal';
import { TableSkeleton } from './components/table-skeleton/table-skeleton';
import { Tabs } from './components/tabs';
import { UniversalSettingsToggle } from './components/universal-settings-toggle';
import type { MenuOptionType } from './config';
import { useMyViewSettingsScope } from './hooks/use-my-view-settings-scope';
import { useMyMetrics } from './hooks/useMyMetrics';
import { OverrideCell } from './override-cell';
import { DEFAULT_COL_WIDTH } from './recReviewColumns';
import {
	calcTotalForecast,
	daysOfWeekSort,
	formattedCurrency,
	renderLiveData,
	renderPriority,
	renderRecStatusCellStyle,
	twoDecimalRateFormatter,
} from './tableCustomCellRender';
import { type ColumnObj, useDashboardColumns } from './useDashboardColumns';
import {
	type FavClickParams,
	useDashboardController,
} from './useDashboardController';

interface DashboardContentProps {
	askRevFilters: AskRevFilter[];
	startDate: string | undefined;
	paginationMaxDuration: number;
	askRevQueryLoading?: boolean;
	// NEW: AI sorting parameters
	aiSortBy?: string | null;
	aiSortDirection?: 'asc' | 'desc' | null;
	aiSortLimit?: number | null;
}

export type Competitors =
	| {
			comp_name_1: string;
			comp_name_2: string;
			comp_name_3: string;
			comp_name_4: string;
			comp_name_5: string;
			comp_name_6: string;
			comp_name_7: string;
			comp_name_8: string;
			comp_name_9: string;
			comp_name_10: string;
			comp_id_1: string;
			comp_id_2: string;
			comp_id_3: string;
			comp_id_4: string;
			comp_id_5: string;
			comp_id_6: string;
			comp_id_7: string;
			comp_id_8: string;
			comp_id_9: string;
			comp_id_10: string;
	  }
	| undefined;

const GreenSwitch = styled(Switch)(() => ({
	'& .MuiSwitch-switchBase': {
		color: styles.colorGreen,
	},
	'& .MuiSwitch-switchBase + .MuiSwitch-track': {
		backgroundColor: styles.colorGreen,
	},
}));

export const DashboardContent = ({
	askRevFilters,
	paginationMaxDuration,
	askRevQueryLoading = false,
	aiSortBy,
	aiSortDirection,
	aiSortLimit,
}: DashboardContentProps) => {
	const dispatch = useAppDispatch();
	const { tab } = useParams() as { tab: string };
	const activeTab = useAppSelector(selectRecReviewTab);
	const pendingRecs = useAppSelector(selectPendingRecs);
	const allRowsSelected = useAppSelector(selectAllRowsSelected);
	const polling = useAppSelector(selectPolling);
	const uploading = useAppSelector(selectUploading);
	const property = useAppSelector(selectProperty);
	const { propertyId, currencyCode } = property;
	const { width } = useWindowDimensions();
	const [pinnedVal, setPinnedVal] = useState<
		boolean | 'left' | 'right' | null | undefined
	>('left');
	const {
		settingsScope,
		isUpdatingSettingsScope,
		isPendingUniversalSwitch,
		handleScopeToggle,
		handleConfirmUniversalSwitch,
		handleCancelUniversalSwitch,
	} = useMyViewSettingsScope({ propertyId });
	const ICON_COL_WIDTH = 45;
	const history = useHistory();
	useEffect(() => {
		history.push(`/my-view/${activeTab}`);
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const { user, isReadOnly } = useUser();
	const userId = user?.id;

	const { showPast, toggleShowPast } = usePropertyContext();

	useEffect(() => {
		if (width) {
			const val = width <= 980 ? undefined : 'left';
			setPinnedVal(val);
		}
	}, [width]);

	const {
		handleRateCellChange,
		handleResetRates,
		handleSelectAll,
		handleSelectNone,
		handleTriggerUpload,
	} = useRateUpload();

	const { locale } = useUser();

	const showAskRev = useAppSelector(selectShowAskRev);
	const { exportData, exportHeader, setExportData, setExportHeader } =
		useExportContext();

	const {
		data,
		compSet,
		loading,
		missingStatusMap,
		refetchRateShop,
		roomTypes,
		aiSorting,
	} = useDashboardController({
		propertyId,
		askRevFilters,
		aiSortBy,
		aiSortDirection,
		aiSortLimit,
	});

	const actionsDisabled = loading || !data.length;

	const { data: intradayForecast } = useGetIntradayForecastQuery({
		skip: !propertyId,
		variables: { propertyId },
	});

	const { data: recRateExplainers, loading: loadingRecRateExplainers } =
		useGetRecRateQueryExplainersQuery({
			skip: !propertyId,
			variables: {
				propertyId,
				dateRange: {
					startDate: todaySubtract(1, 'week'),
					endDate: addDaysStr(paginationMaxDuration, today()),
				},
			},
		});

	const fixedColumns: ColumnObj[] = [
		{
			headerName: '',
			field: 'sellout_ind',
			headerComponentParams: { headerIcon: 'fa-flag' },
			suppressMovable: true,
			headerTooltip: labels.rec_review.dashboard.tooltip.alert,
			pinned: pinnedVal,
			lockPinned: true,
			lockPosition: true,
			width: ICON_COL_WIDTH,
			cellRenderer: (params) => {
				const t = RecAlert(params);
				return t;
			},
		},
		{
			headerName: '',
			headerComponentParams: { headerIcon: 'fa-circle' },
			field: 'priority',
			suppressMovable: true,
			pinned: pinnedVal,
			lockPinned: true,
			lockPosition: true,
			width: ICON_COL_WIDTH,
			headerTooltip: labels.rec_review.dashboard.tooltip.priority,
			cellRenderer: (params: { value: number }) => {
				const t = renderPriority(params.value);
				return t;
			},
		},
		{
			headerName: 'Date',
			field: 'stay_date',
			suppressMovable: true,
			lockPosition: true,
			pinned: pinnedVal,
			lockPinned: true,
			width: DEFAULT_COL_WIDTH - 40,
			headerComponentParams: { field: 'stay_date' },
			headerTooltip: labels.rec_review.dashboard.tooltip.date,
			cellRenderer: (params: ICellRendererParams) => {
				return <StayDateWithSeasonTooltip params={params} />;
			},
		},
		{
			headerName: 'DOW',
			field: 'dow',
			pinned: pinnedVal,
			lockPinned: true,
			width: DEFAULT_COL_WIDTH - 50,
			suppressMovable: true,
			lockPosition: true,
			headerTooltip: labels.rec_review.dashboard.tooltip.dow,
			comparator: (valueA: string, valueB: string) => {
				return daysOfWeekSort(valueA, valueB);
			},
		},
		{
			headerName: '',
			headerComponentParams: { headerIcon: <IoStar size={20} /> },
			field: 'is_favorite',
			pinned: pinnedVal,
			lockPinned: true,
			width: ICON_COL_WIDTH,
			headerTooltip: labels.rec_review.dashboard.tooltip.favorite,
			suppressMovable: true,
			lockPosition: true,
			cellRenderer: (params: ICellRendererParams & FavClickParams) => {
				return <FavStayDate params={params} />;
			},
			valueSetter: (params: ValueSetterParams) => {
				params.node?.setData({
					...params.node?.data,
					is_favorite: params.newValue,
				});
				return params.newValue;
			},
		},
		{
			headerName: 'LVL A\nStatus',
			field: 'restriction_type',
			headerTooltip: 'Current stay restrictions at rate level A',
			pinned: pinnedVal,
			lockPinned: true,
			lockPosition: true,
			suppressMovable: true,
			width: DEFAULT_COL_WIDTH - 30,
			cellRenderer: RestrictionCell,
			sortable: true,
		},
		// Hidden from display until fully implemented per B20V-2035:
		// {
		//   headerName: '',
		//   field: 'event',
		//   headerComponentParams: { headerIcon: 'fa-calendar' },
		//   suppressMovable: true,
		//   lockPosition: true,
		//   pinned: pinnedVal,
		//   lockPinned: true,
		//   width: 60,
		//   headerTooltip: labels.rec_review.dashboard.tooltip.event,
		//   tooltipField: 'event.eventDescription',
		// },
		{
			headerName: 'BWH\nRate',
			headerTooltip: labels.rec_review.dashboard.tooltip.bw_rate,
			field: 'current_rate_live',
			width: DEFAULT_COL_WIDTH - 20,
			pinned: pinnedVal,
			lockPinned: true,
			suppressMovable: true,
			lockPosition: true,
			cellRenderer: (params: ICellRendererParams) => {
				return renderLiveData(
					{ ...params },
					'current_rate_live',
					'current_rate',
					true,
					twoDecimalRateFormatter,
				);
			},
			comparator: (
				liveRateA: number,
				liveRateB: number,
				nodeA,
				nodeB,
				isDescending,
			) => {
				const rateA = liveRateA ?? nodeA.data?.current_rate;
				const rateB = liveRateB ?? nodeB.data?.current_rate;

				if (rateA === null) {
					return isDescending ? -1 : 1;
				}
				return rateA - rateB;
			},
		},
		{
			headerName: (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<span style={{ display: 'inline-block' }}>{'Rec\nRate'}</span>
					<HelpOutline
						style={{
							display: 'inline-block',
							fontSize: '1rem',
							marginLeft: '2px',
						}}
					/>
				</div>
			),
			field: 'rec_rate',
			pinned: pinnedVal,
			lockPinned: true,
			width: DEFAULT_COL_WIDTH - 20,
			suppressMovable: true,
			lockPosition: true,
			headerTooltip: labels.rec_review.dashboard.tooltip.rec_rate,
			tooltipComponent: ({ value }: { value: string }) => {
				return (
					<div
						style={{
							border: '1px solid #babfc7',
							borderRadius: '3px',
							background: 'white',
							width: '11rem',
							padding: '6px 4px',
						}}
					>
						<div>{value}</div>
						<p
							style={{
								margin: '6px',
								fontSize: '0.75rem',
								color: styles.colorGrey80,
							}}
						>
							{labels.rec_review.dashboard.tooltip.rec_rate_desc}
						</p>
					</div>
				);
			},
			cellRenderer: (params: ICellRendererParams) => {
				const firstThreeWeeks = dayRange(today(), todayAdd(21, 'days'));
				const forecastData = intradayForecast?.getIntradayForecast?.find(
					(forecast) => dataDate(forecast?.stay_date) === params.data.stay_date,
				);
				const explainerData = recRateExplainers?.getRecRateExplainers?.find(
					(explainer) => explainer?.stay_date === params.data?.stay_date,
				);
				const recRate = data.find(
					(d) => d.stay_date === params.data.stay_date,
				)?.rec_rate;

				// Check if rec_rate hits season boundaries
				const season = params.data?.season;
				const showBoundaryWarning =
					recRate &&
					season &&
					season.min_rate !== null &&
					season.max_rate !== null &&
					(recRate >= season.max_rate || recRate <= season.min_rate);
				if (firstThreeWeeks.includes(params.data.stay_date) && forecastData) {
					return (
						<ForecastInfoTooltip
							data={{
								newRecRate: recRate,
								prevRecRate: forecastData.prev_rec_rate,
								currentRate: forecastData.current_rate,
								changeInRecRate: forecastData.rate_variance,
								pricingRatio: explainerData?.pricing_ratio,
								pickupRatio: explainerData?.pickup_ratio,
								incrementalRevenue: explainerData?.incr_revenue,
								changeInMarketRate: forecastData.delta_market_rate,
								reasons: forecastData.reasons,
								lastHourPickup: forecastData.last_hour_pkp,
								expectedPickup: forecastData.expected_pkp,
								actualPickup: forecastData.actual_pkp,
								expectedAllDayPickup: forecastData.expected_allday_pkp,
								expectedAllDayPickupUnconstrained:
									forecastData.expected_allday_pkp_unc,
								snapshotHour: forecastData.snapshot_hour,
								lastUpdatedDate: forecastData.last_updated_date,
							}}
						>
							<div
								style={{ display: 'flex', alignItems: 'center', gap: '2px' }}
							>
								{formattedCurrency(
									params.value,
									locale,
									currencyCode,
									true,
									twoDecimalRateFormatter,
								)}
								{showBoundaryWarning && (
									<span style={{ color: '#d32f2f', fontWeight: 'bold' }}>
										*
									</span>
								)}
							</div>
						</ForecastInfoTooltip>
					);
				}
				return (
					<PriceDecisionTooltip
						data={recRateExplainers?.getRecRateExplainers?.find(
							(explainer) => explainer?.stay_date === params.data?.stay_date,
						)}
						loading={loadingRecRateExplainers}
						recRate={params.data?.rec_rate}
						bwhRate={
							params.data?.current_rate_live ?? params.data?.current_rate
						}
					>
						<div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
							{formattedCurrency(
								params.value,
								locale,
								currencyCode,
								true,
								twoDecimalRateFormatter,
							)}
							{showBoundaryWarning && (
								<span style={{ color: '#d32f2f', fontWeight: 'bold' }}>*</span>
							)}
						</div>
					</PriceDecisionTooltip>
				);
			},
		},
		{
			headerName: 'Rate to\nUpload',
			field: 'override_rate',
			width: DEFAULT_COL_WIDTH - 20,
			editable: (params: EditableCallbackParams) => {
				const isPast = params.data.stay_date < today();
				return !isPast && !isReadOnly;
			},
			pinned: pinnedVal,
			lockPinned: true,
			suppressMovable: true,
			lockPosition: true,
			headerTooltip: labels.rec_review.dashboard.tooltip.rate_to_upload,
			singleClickEdit: true,
			cellRenderer: (params: ICellRendererParams) => {
				const isPast = params.data.stay_date < today();
				return isPast ? null : <OverrideCell {...params} />;
			},
			cellEditorSelector: (params: ICellEditorParams) => {
				const isPast = params.data.stay_date < today();
				return isPast
					? undefined
					: {
							component: 'numericCellEditor',
							params: {
								currencyCode,
								locale,
								twoDecimalRateFormatter,
							},
						};
			},
			onCellValueChanged: (params: NewValueParams) => {
				handleRateCellChange(params);
			},
			valueFormatter: (params: ValueFormatterParams) => {
				return formattedCurrency(
					params.value,
					locale,
					currencyCode,
					true,
					twoDecimalRateFormatter,
				);
			},
			valueSetter: (params: ValueSetterParams) => {
				params.node?.setData({
					...params.node?.data,
					override_rate: params.newValue,
				});
				return params.newValue;
			},
		},

		{
			headerName: 'Rec\nStatus',
			field: 'rec_status',
			pinned: pinnedVal,
			lockPinned: true,
			headerTooltip: labels.rec_review.dashboard.tooltip.rec_status,
			suppressMovable: true,
			lockPosition: true,
			width: DEFAULT_COL_WIDTH - 20,
			cellRenderer: (params: any) => {
				const recData = data.find((d) => d.stay_date === params.data.stay_date);
				const rec_status = recData?.rec_status;
				const last_updated_by = recData?.last_updated_by;
				const autopilot_ind = recData?.autopilot_ind;
				const isAutopilotPending = isRecStatusAutopilotPending(
					rec_status,
					last_updated_by,
					autopilot_ind,
				);

				return renderRecStatusCellStyle(params, isAutopilotPending);
			},
		},
		{
			headerName: 'Market\nRate',
			headerTooltip: labels.rec_review.dashboard.tooltip.market_rate,
			field: 'market_rate',
			suppressMovable: true,
			lockPosition: true,
			pinned: pinnedVal,
			lockPinned: true,
			width: DEFAULT_COL_WIDTH - 20,
			/*  valueFormatter: (params: ValueFormatterParams) => {
        return twoDecimalRateFormatter(params.value);
      }, */
			cellRenderer: (params: ICellRendererParams) => {
				return (
					<RateCellRender
						params={params}
						digitFormatter={twoDecimalRateFormatter}
					/>
				);
			},
			headerClass: ['age-border'],
			cellClass: ['age-border'],
		},
	];

	const {
		competitorCols,
		forecastCols,
		inventoryCols,
		kpiCols,
		metricsCols,
		pickupCols,
		ratesCols,
		roomTypeRatesCols,
		handleColumnMoved,
		addColumnToMetric,
	} = useDashboardColumns(
		fixedColumns.length,
		compSet,
		roomTypes,
		settingsScope,
	);

	const { isShown: isResetModalShown, toggle: toggleResetModal } = useModal();

	const { updateUserHotelMetrics } = useMyMetrics({
		propertyId: propertyId!,
		userId: userId!,
	});

	const handleResetColumns = () => {
		const defaultTabs = {
			metrics: [
				'available_rooms_live',
				'transient_capacity_live',
				'total_fcst_incl_group',
				'current_otb_live',
				'adr_live',
				'revpar_live',
				'occ_pct_live',
				'fcst_occ_pct',
			],
		};

		updateUserHotelMetrics({
			variables: {
				userId: userId!,
				propertyId: propertyId!,
				metricCols: JSON.stringify({
					tabs: defaultTabs,
				}),
			},
		});
		dispatch(setMyMetrics(undefined));
		dispatch(setTabs(defaultTabs));
		toggleResetModal();
	};

	const gridRef = useRef<AgGridReact>(null);

	useEffect(() => {
		if (gridRef.current && propertyId) {
			gridRef.current.api?.forEachNode((row) => {
				if (row.data?.rec_status === 'Pending') {
					row.setSelected(true);
				} else {
					row.setSelected(false);
				}
			});
		}
	}, [gridRef, propertyId]);

	const MoreOptionEleIcon: React.FC = () => (
		<MdMoreHoriz size={22} className='dashboard__options-icon' />
	);

	const onSelectAllRows = () => {
		const selectAll = !allRowsSelected;

		if (selectAll) {
			handleSelectAll(gridRef);
			dispatch(setAllRowsSelected(selectAll));
		} else {
			handleSelectNone(gridRef);
			dispatch(setAllRowsSelected(selectAll));
		}
	};

	const onRateResetClick = () => {
		handleResetRates(gridRef);
	};

	const getColumns = () => {
		if (activeTab === 'forecast') {
			const column = [...fixedColumns, ...forecastCols];
			return column;
		}
		if (activeTab === 'metrics') {
			const column = [...fixedColumns, ...metricsCols];
			return column;
		}
		if (activeTab === 'inventory') {
			const column = [...fixedColumns, ...inventoryCols];
			return column;
		}
		if (activeTab === 'pickup') {
			const column = [...fixedColumns, ...pickupCols];
			return column;
		}
		if (activeTab === 'rates') {
			const column = [...fixedColumns, ...ratesCols];
			return column;
		}
		if (activeTab === 'kpi') {
			const column = [...fixedColumns, ...kpiCols];
			return column;
		}
		if (activeTab === 'roomTypeRates') {
			const column = [...fixedColumns, ...roomTypeRatesCols];
			return column;
		}
		if (activeTab === 'competitors' && compSet) {
			const allCompetitorsCols = competitorCols(compSet) || [];
			const filterCompetitorsCols = allCompetitorsCols.filter(
				(c) => !!c.headerName && !!c.headerTooltip,
			);
			const column = [...fixedColumns, ...filterCompetitorsCols];
			return column;
		}
		if (compSet) {
			const allCompetitorsCols = competitorCols(compSet) || [];
			const filterCompetitorsCols = allCompetitorsCols.filter(
				(c) => !!c.headerName && !!c.headerTooltip,
			);
			const column = [...fixedColumns, ...filterCompetitorsCols];
			return column;
		}
		return fixedColumns;
	};

	const tableColumn = getColumns();

	const getFileName = useMemo(() => {
		const date = new Date();
		const dateFormat = `${date.getDate()}_${date.toString().substr(4, 3)}_${date.getFullYear()}_${date.getHours()}${date.getMinutes()}${date.getSeconds()}`;
		return `${propertyId}_${tab}_${dateFormat}.csv`;
	}, [propertyId, tab]);

	const replaceNullOrZeroWithDash = (value: number | null | undefined) => {
		return value !== null && value !== undefined && value !== 0 ? value : '-';
	};

	const processDataForCSV = useCallback(() => {
		return exportData.map((item: any) => {
			const totalForecast = calcTotalForecast(item);
			// Get all live data and format it to be exported
			const liveData = pickBy(item, (_value, key) => key.includes('_live'));
			// Remove _live from the key and keep live data if present
			const newObject: { [key: string]: number | null } = {};
			for (const key of Object.keys(liveData)) {
				const formattedKey = key.replace('_live', '');
				newObject[formattedKey] = liveData[key];
			}
			return {
				...item,
				...newObject,
				a_fcst_sell: `${replaceNullOrZeroWithDash(
					item.a_current_otb,
				)}/${replaceNullOrZeroWithDash(
					item.a_fcst_sell,
				)}/${replaceNullOrZeroWithDash(item.a_status)}`,
				b_fcst_sell: `${replaceNullOrZeroWithDash(
					item.b_current_otb,
				)}/${replaceNullOrZeroWithDash(
					item.b_fcst_sell,
				)}/${replaceNullOrZeroWithDash(item.b_status)}`,
				c_fcst_sell: `${replaceNullOrZeroWithDash(
					item.c_current_otb,
				)}/${replaceNullOrZeroWithDash(
					item.c_fcst_sell,
				)}/${replaceNullOrZeroWithDash(item.c_status)}`,
				d_fcst_sell: `${replaceNullOrZeroWithDash(
					item.d_current_otb,
				)}/${replaceNullOrZeroWithDash(
					item.d_fcst_sell,
				)}/${replaceNullOrZeroWithDash(item.d_status)}`,
				e_fcst_sell: `${replaceNullOrZeroWithDash(
					item.e_current_otb,
				)}/${replaceNullOrZeroWithDash(
					item.e_fcst_sell,
				)}/${replaceNullOrZeroWithDash(item.e_status)}`,
				f_fcst_sell: `${replaceNullOrZeroWithDash(
					item.f_current_otb,
				)}/${replaceNullOrZeroWithDash(
					item.f_fcst_sell,
				)}/${replaceNullOrZeroWithDash(item.f_status)}`,
				g_fcst_sell: `${replaceNullOrZeroWithDash(
					item.g_current_otb,
				)}/${replaceNullOrZeroWithDash(
					item.g_fcst_sell,
				)}/${replaceNullOrZeroWithDash(item.g_status)}`,
				h_fcst_sell: `${replaceNullOrZeroWithDash(
					item.h_current_otb,
				)}/${replaceNullOrZeroWithDash(
					item.h_fcst_sell,
				)}/${replaceNullOrZeroWithDash(item.h_status)}`,
				total_fcst_incl_group: totalForecast?.total_fcst_incl_group,
				total_fcst_incl_group_live: totalForecast?.total_fcst_incl_group_live,
				fcst_occ_pct:
					totalForecast?.fcst_occ_pct_live !== undefined
						? totalForecast.fcst_occ_pct_live.toFixed(1)
						: totalForecast?.fcst_occ_pct?.toFixed(1),
			};
		});
	}, [exportData]);

	useEffect(() => {
		const headers = getCsvFormatHeader(tableColumn);

		const exportHeaders = headers.map((header) => {
			if (header.key.includes('live')) {
				const keyMapping = {
					transient_capacity: 'transient_cap',
					ooo: 'out_of_order',
				};

				const mappedKey =
					keyMapping[
						header.key.replace('_live', '') as keyof typeof keyMapping
					] || header.key.replace('_live', '');
				return {
					label: header.label,
					key: mappedKey,
				};
			}
			if (header.key.includes('compRates')) {
				return {
					label: header.label,
					key: `${header.key}.value`,
				};
			}
			return header;
		});

		setExportHeader?.(exportHeaders);
		setExportData?.(data);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, tab]);

	const [missingWarningCalled, setMissingWarningCalled] = useState(false);
	useEffect(() => {
		if (missingStatusMap.size > 1 && !loading && !missingWarningCalled) {
			setMissingWarningCalled(true);
			dispatch(
				alertAdded(
					'warning',
					labels.rec_review.dashboard.missing_status_alert,
					'/help/94b9fc1c-dc32-43bb-9529-e03bf4b7630b',
				),
			);
		}
		return () => {
			setMissingWarningCalled(false);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, loading]);

	const agGridData = useMemo(() => {
		const noData: any[] = [];

		const noAskRevId = showAskRev && askRevFilters.length === 0;

		return {
			column: tableColumn,
			data: noAskRevId ? noData : data,
			showMessage: noAskRevId ? labels.ask_best_rev.initial_message : '',
		};
	}, [data, showAskRev, askRevFilters, tableColumn]);

	const { isShown: isRateShopModalShown, toggle: toggleRateShopModal } =
		useModal();

	const moreOptions: Array<MenuOptionType> = [
		/* TODO B20V-2284 Hiding moreOptions until they are ready */
		//...moreOptionItems,
		{
			id: 6,
			title: labels.rec_review.dashboard.more_action.reset_columns.header,
			disabled: actionsDisabled,
			onClick: toggleResetModal,
		},
		{
			id: 7,
			title: 'Rate Shop Options',
			onClick: toggleRateShopModal,
		},
	];

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 5,
			},
		}),
	);

	function handleDragEnd(event: DragEndEvent) {
		const { over, active } = event;
		const { action, source } = active.data.current ?? {};

		if (over && action === 'add') {
			addColumnToMetric(active.id as string, source);
		}
	}

	return (
		<>
			<Modal
				isShown={isPendingUniversalSwitch}
				hide={handleCancelUniversalSwitch}
				headerText={
					labels.rec_review.dashboard.more_action.universal_switch.header
				}
				messageBody={
					labels.rec_review.dashboard.more_action.universal_switch.message
				}
				isConfirmationModal={true}
				confirmBtnText={
					labels.rec_review.dashboard.more_action.universal_switch.confirm
				}
				cancelBtnText={
					labels.rec_review.dashboard.more_action.universal_switch.cancel
				}
				onConfirm={handleConfirmUniversalSwitch}
				onCancel={handleCancelUniversalSwitch}
				loading={isUpdatingSettingsScope}
			/>
			<Modal
				isShown={isResetModalShown}
				hide={toggleResetModal}
				headerText={
					labels.rec_review.dashboard.more_action.reset_columns.header
				}
				messageBody={
					labels.rec_review.dashboard.more_action.reset_columns.message
				}
				isConfirmationModal={true}
				confirmBtnText={
					labels.rec_review.dashboard.more_action.reset_columns.confirm
				}
				cancelBtnText={
					labels.rec_review.dashboard.more_action.reset_columns.cancel
				}
				onConfirm={handleResetColumns}
				onCancel={toggleResetModal}
			/>
			<RateShopModal
				isShown={isRateShopModalShown}
				toggle={toggleRateShopModal}
				refetchRateShop={refetchRateShop}
			/>
			<DndContext sensors={sensors} onDragEnd={handleDragEnd}>
				<div className='dashboard__header'>
					<div className='tw-flex tw-flex-row tw-gap-2'>
						<PendingRecsStatusMessage />
						<RateChangeJobStatus />
					</div>

					<div className='tw-flex tw-justify-between'>
						<div className='tw-flex tw-items-center'>
							<CustomTooltip
								content={
									<p>{labels.rec_review.dashboard.more_action.select_all}</p>
								}
							>
								<span>
									<IconButton
										disableFocusRipple
										disableRipple
										aria-label='all-rows'
										className='tw-bg-transparent tw-border-none tw-p-2 tw-flex tw-items-center tw-justify-center tw-mx-1 tw-text-center tw-cursor-pointer tw-text-[hsl(var(--burgundy))]'
										onClick={onSelectAllRows}
										disabled={actionsDisabled || isReadOnly}
									>
										<MdCheckCircleOutline
											size={22}
											style={{ color: '#905959' }}
										/>
									</IconButton>
								</span>
							</CustomTooltip>
							<RateUploadButton
								disabled={
									!(pendingRecs && pendingRecs.length > 0 && !isReadOnly)
								}
								handleTriggerUpload={() => {
									console.log('handleTriggerUpload', gridRef.current);
									return handleTriggerUpload(gridRef);
								}}
								polling={polling}
								uploading={uploading}
								className='tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-center tw-mx-1'
							/>
							<CustomTooltip
								content={
									<p>{labels.rec_review.dashboard.more_action.refresh}</p>
								}
							>
								<span>
									<IconButton
										disableFocusRipple
										disableRipple
										aria-label='reset'
										className='tw-bg-transparent tw-border-none tw-p-2 tw-flex tw-items-center tw-justify-center tw-mx-1 tw-text-center tw-cursor-pointer tw-text-[hsl(var(--burgundy))]'
										onClick={onRateResetClick}
										disabled={
											!(pendingRecs && pendingRecs.length > 0 && !isReadOnly)
										}
									>
										<MdRefresh size={22} style={{ color: '#905959' }} />
									</IconButton>
								</span>
							</CustomTooltip>

							<CustomTooltip
								content={<p>{labels.rec_review.dashboard.more_action.graph}</p>}
							>
								<IconButton
									disableFocusRipple
									disableRipple
									aria-label='graph'
									className='tw-bg-transparent tw-border-none tw-p-2 tw-flex tw-items-center tw-justify-center tw-mx-1 tw-text-center tw-cursor-pointer tw-text-[hsl(var(--burgundy))]'
								>
									<Link to='/my-view/graph'>
										<BsFillFileBarGraphFill
											size={22}
											style={{ color: '#905959' }}
										/>
									</Link>
								</IconButton>
							</CustomTooltip>

							<CustomTooltip
								content={
									<p>{labels.rec_review.dashboard.more_action.export}</p>
								}
							>
								<div>
									<ExportCSV
										csvHeaders={exportHeader}
										csvData={processDataForCSV()}
										fileName={getFileName}
										content={
											<IconButton
												disableFocusRipple
												disableRipple
												aria-label='export'
												className='tw-bg-transparent tw-border-none tw-p-2 tw-flex tw-items-center tw-justify-center tw-mx-1 tw-text-center tw-cursor-pointer tw-text-[hsl(var(--green))]'
												disabled={actionsDisabled}
											>
												<MdSimCardDownload
													size={22}
													style={{ color: '#55c652' }}
												/>
											</IconButton>
										}
									/>
								</div>
							</CustomTooltip>
							<CustomTooltip
								content={
									<p>
										{showPast
											? labels.rec_review.dashboard.more_action.hide_past
											: labels.rec_review.dashboard.more_action.show_past}
									</p>
								}
							>
								<GreenSwitch
									checked={showPast}
									onChange={toggleShowPast}
									size='small'
								/>
							</CustomTooltip>
							<CustomTooltip
								content={
									<p>{labels.rec_review.dashboard.more_action.more_options}</p>
								}
							>
								<div className='dashboard__options-more'>
									<DropdownList
										content={<MoreOptionEleIcon />}
										menu={moreOptions}
									/>
								</div>
							</CustomTooltip>
						</div>
						<div className='tw-flex tw-items-center'>
							<UniversalSettingsToggle
								isUniversal={settingsScope === SettingsScope.universal}
								onToggle={handleScopeToggle}
								disabled={isUpdatingSettingsScope}
							/>
							<Tabs />
						</div>
					</div>
				</div>
				<div className='dashboard__tables'>
					{(showAskRev && loading) || askRevQueryLoading ? (
						<TableSkeleton
							rows={15}
							columns={Math.min(agGridData.column.length, 10)}
							className='tw-rounded-lg tw-shadow-sm'
						/>
					) : (
						<LoadingBar
							loading={loading}
							overlayStyle={{
								borderRadius: '12px',
								backgroundColor: ' rgba(0, 0, 0, 0.15)',
							}}
						>
							<DashboardTable
								columns={agGridData.column}
								data={agGridData.data}
								gridRef={gridRef}
								paginationMaxDuration={paginationMaxDuration}
								handleColumnMoved={handleColumnMoved}
								fixedColsLength={fixedColumns.length}
								showMessage={agGridData.showMessage}
								aiSorting={aiSorting}
							/>
						</LoadingBar>
					)}
				</div>
			</DndContext>
		</>
	);
};
